import {
  Button,
  Calendar,
  HeartCalendarIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sidebar,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useSidebar,
} from "@nfp-tracker/components";
import { addDays, format, parseISO } from "date-fns";
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Crown,
  LifeBuoy,
  LogOut,
  Maximize2,
  Sparkles,
  Table2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ClientSwitcher } from "../components/ClientSwitcher";
import { ClientViewBanner } from "../components/ClientViewBanner";
import HealthDisclaimer from "../components/HealthDisclaimer";
import { PostHogSurveyModal } from "../components/PostHogSurveyModal";
import AnalyticsPersonProfileSync from "../components/AnalyticsPersonProfileSync";
import { SupportChatProvider } from "../components/SupportChat";
import { useSupportChatPanel } from "../components/SupportChatContext";
import { CALENDAR_FULLSCREEN_EVENT } from "../constants/events";
import {
  getInstructorClientTabFromPath,
  isPersonalTrackingRoute,
  ROUTES,
} from "../constants/routes";
import { trackEvent } from "../integrations/analytics/client";
import { usePostHogSurvey } from "../hooks/usePostHogSurvey";
import { useObservationsRealtimeSync } from "../hooks/useObservationsRealtimeSync";
import {
  readOnboardingCompleteCache,
  useOnboardingChecklist,
} from "../hooks/useOnboardingChecklist";
import { useActorRole, useOwnerContext } from "../hooks/useOwnerContext";
import { useSubscriptionStorefrontAccess } from "../hooks/useSubscriptionManagementAvailability";
import { useSubscription } from "../hooks/useSubscription";
import { supabase } from "../integrations/supabase/client";
import { NewBadge } from "../components/NewBadge";
import { useCurrentUserWhatsNew } from "../hooks/useCurrentUserWhatsNew";
import { AppPageHeader } from "./AppPageHeader";
import { AppSidebarNav } from "./AppSidebarNav";
import { getPageMeta } from "./navigation";

const LAST_SAVED_EVENT = "observations-last-saved-updated";

const parseDateParam = (value: string | null): string | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : value;
};

const formatLastSavedSubtitle = (isoDate: string | null): string => {
  if (!isoDate) return "Changes save automatically.";

  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return "Changes save automatically.";

  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);

  return `Last saved at ${timeLabel}`;
};

const AppShellLayout = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const pageMeta = getPageMeta(pathname);
  const [todaySubtitle, setTodaySubtitle] = useState("Changes save automatically.");
  const [sidebarDisplayName, setSidebarDisplayName] = useState("NFP Tracker");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const {
    activeSurvey,
    isOpen: isSurveyOpen,
    submitResponse: handleSurveySubmit,
    dismissSurvey: handleSurveyDismiss,
  } = usePostHogSurvey();

  // Getting Started drops out of the main nav (AppSidebarNav) once complete; this
  // resurfaces it here so users can still get back to it. Falls back to the cached
  // flag while the live state loads, matching AppSidebarNav's own flash-avoidance.
  const { loading: onboardingLoading, allComplete: onboardingComplete } =
    useOnboardingChecklist();
  const { hasNew: hasWhatsNew, markPathVisited } = useCurrentUserWhatsNew();
  useEffect(() => {
    markPathVisited(pathname);
  }, [pathname, markPathVisited]);
  const showGettingStartedFooterLink = onboardingLoading
    ? readOnboardingCompleteCache() === true
    : onboardingComplete;

  // Shared, session-cached auth/owner/permission context — dedupes against whatever
  // page-level useOwnerContext() call already warmed this cache, instead of re-running
  // the owner-resolution chain again just to populate the sidebar.
  const ownerContext = useOwnerContext().data;
  const couple = ownerContext?.status === "ready" ? ownerContext.couple : null;
  const sidebarOwnerUserId = couple?.id;
  const actorUserId = ownerContext?.status === "ready" ? ownerContext.actorUserId : undefined;
  // The account's own role (live, or this user's persisted hint on a cold load), so role-specific
  // chrome never renders as the personal variant for an instructor while the context loads.
  const { role: actorRole, isResolved: isActorRoleResolved } = useActorRole();
  const isInstructorAccount = actorRole === "instructor";
  const showPersonalChrome = isActorRoleResolved && !isInstructorAccount;
  const viewingClientUserId =
    ownerContext?.status === "ready" ? ownerContext.viewingClientUserId : null;
  const isInstructorRoute = pathname.startsWith(ROUTES.instructorClients);
  const clientTab = viewingClientUserId ? getInstructorClientTabFromPath(pathname) : null;
  const hasSpouse = Boolean(couple?.partner_user_id);
  const isObservationsRoute =
    pathname.startsWith(ROUTES.observations) || pathname.startsWith(ROUTES.legacyToday);
  const isCalendarRoute = pathname.startsWith(ROUTES.calendar);
  // The shell's single subscription instance; the analytics profile sync reads it too rather than
  // running a second poller / realtime channel of its own.
  const subscription = useSubscription(sidebarOwnerUserId);
  const { status: subscriptionStatus, daysRemaining } = subscription;
  const { isSubscriptionManagementAvailable } =
    useSubscriptionStorefrontAccess();

  const searchParams = new URLSearchParams(search);
  const requestedDate = parseDateParam(searchParams.get("date"));
  const todayDate = format(new Date(), "yyyy-MM-dd");
  const viewedDate = requestedDate ?? todayDate;
  const isViewingToday = viewedDate === todayDate;
  const viewedDateLabel = format(parseISO(viewedDate), "EEE, MMM d, yyyy");

  const goToObservationDate = (dateKey: string) => {
    if (dateKey === todayDate) {
      navigate(ROUTES.observations);
      return;
    }

    navigate(`${ROUTES.observations}?date=${dateKey}`);
  };

  const previousDate = format(addDays(parseISO(viewedDate), -1), "yyyy-MM-dd");
  const nextDate = format(addDays(parseISO(viewedDate), 1), "yyyy-MM-dd");

  useEffect(() => {
    if (!isObservationsRoute) return;

    setTodaySubtitle("Changes save automatically.");

    const handleLastSavedUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<string | null>;
      setTodaySubtitle(formatLastSavedSubtitle(customEvent.detail));
    };

    window.addEventListener(LAST_SAVED_EVENT, handleLastSavedUpdate as EventListener);

    return () => {
      window.removeEventListener(LAST_SAVED_EVENT, handleLastSavedUpdate as EventListener);
    };
  }, [isObservationsRoute]);

  useEffect(() => {
    const loadSidebarName = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        return;
      }

      const metadataFirstName = (user.user_metadata?.first_name as string | undefined)?.trim() || "";
      const metadataLastName = (user.user_metadata?.last_name as string | undefined)?.trim() || "";
      const metadataFullName = `${metadataFirstName} ${metadataLastName}`.trim();
      if (metadataFullName) {
        setSidebarDisplayName(metadataFullName);
      }

      const { data: account } = await supabase
        .from("user_accounts")
        .select("first_name, last_name")
        .eq("user_id", user.id)
        .maybeSingle();

      const accountFirstName =
        (account as { first_name?: string | null } | null)?.first_name?.trim() || "";
      const accountLastName =
        (account as { last_name?: string | null } | null)?.last_name?.trim() || "";
      const accountFullName = `${accountFirstName} ${accountLastName}`.trim();

      if (accountFullName) {
        setSidebarDisplayName(accountFullName);
      }
    };

    void loadSidebarName();

    // Settings name edits call auth.updateUser, which emits USER_UPDATED.
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "USER_UPDATED") void loadSidebarName();
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // The instructor routes live in this same shell, so the role gate lives here too: a tracking
  // account that lands on `/instructor/clients/*` has nothing to see and goes back to its own app.
  useEffect(() => {
    if (!isInstructorRoute || ownerContext?.status !== "ready") return;
    if (ownerContext.actorRole !== "instructor") {
      navigate(ROUTES.observations, { replace: true });
    }
  }, [isInstructorRoute, ownerContext, navigate]);

  // Single shared realtime subscription for the observations cache, mounted once for the
  // whole authenticated session rather than once per page. `ownerUserId` is the resolved owner,
  // so while an instructor has a client open this follows the client's observations.
  useObservationsRealtimeSync({
    ownerUserId: sidebarOwnerUserId,
    actorUserId,
    hasSpouse,
  });

  // Instructor accounts are not billed (see INSTRUCTOR_ACCOUNTS.md), so the access indicator
  // would only ever show a meaningless trial countdown for them.
  const subscriptionIndicatorLabel = !isSubscriptionManagementAvailable || isInstructorAccount
    ? null
    : subscriptionStatus === "trialing"
    ? typeof daysRemaining === "number" && daysRemaining > 0
      ? `${daysRemaining}d Access Left`
      : "Access Active"
    : subscriptionStatus === "past_due"
    ? "Past Due"
    : null;

  const effectivePageMeta =
    pathname === ROUTES.subscription && !isSubscriptionManagementAvailable
      ? getPageMeta(ROUTES.settings)
      : pageMeta;
  // An instructor on a personal tracking route is mid-redirect to the roster (TrackingRouteGate);
  // keep the header blank for that frame rather than titling it "Observations" / today's date.
  const isRedirectingInstructor = isInstructorAccount && isPersonalTrackingRoute(pathname);
  const subtitle = isRedirectingInstructor
    ? ""
    : isObservationsRoute
      ? todaySubtitle
      : effectivePageMeta.subtitle;

  const title = isRedirectingInstructor
    ? ""
    : isObservationsRoute
      ? viewedDateLabel
      : effectivePageMeta.title;
  // The health disclaimer is only shown on the data-heavy screens (Observations
  // and Insights); other pages drop it to reduce footer clutter.
  const showFooterDisclaimer =
    isObservationsRoute || pathname === ROUTES.dashboard;

  const calendarFullscreenAction = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="hidden lg:inline-flex"
          aria-label="Open full-screen calendar"
          onClick={() => {
            trackEvent("calendar_fullscreen_opened", {});
            window.dispatchEvent(new Event(CALENDAR_FULLSCREEN_EVENT));
          }}
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Full screen</TooltipContent>
    </Tooltip>
  );

  // While an instructor has a client open, the header carries the client controls (switch / close)
  // so the read-only banner below stays a single slim line.
  const headerActions = clientTab ? (
    <>
      {clientTab === "calendar" ? calendarFullscreenAction : null}
      <ClientSwitcher />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close client"
            onClick={() => navigate(ROUTES.instructorClients)}
          >
            <X className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Back to clients</TooltipContent>
      </Tooltip>
    </>
  ) : isObservationsRoute && showPersonalChrome ? (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="View previous day"
            onClick={() => goToObservationDate(previousDate)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Previous day</TooltipContent>
      </Tooltip>
      <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-9 px-0 sm:w-auto sm:px-3"
                aria-label="Choose a date"
              >
                <CalendarDays className="h-4 w-4" />
                <span className="sr-only sm:hidden">Choose date</span>
                <span className="hidden sm:inline">Calendar</span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Choose date</TooltipContent>
        </Tooltip>
        <PopoverContent className="w-auto p-0" align="center">
          <div className="border-b p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              disabled={isViewingToday}
              onClick={() => {
                goToObservationDate(todayDate);
                setIsDatePickerOpen(false);
              }}
            >
              Go to Today
            </Button>
          </div>
          <Calendar
            mode="single"
            selected={parseISO(viewedDate)}
            defaultMonth={parseISO(viewedDate)}
            disabled={{ after: new Date() }}
            onSelect={(day) => {
              if (!day) return;
              goToObservationDate(format(day, "yyyy-MM-dd"));
              setIsDatePickerOpen(false);
            }}
            classNames={{
              // The shared Calendar's default `cell` styling is built for range mode — a
              // square accent background spans between selected days, rounded only at the
              // start/end of a run. In single-day mode that shows as a hard square halo
              // behind the (already rounded) selected-day circle. Single mode only ever
              // has one selected day, so drop the range-band styling entirely here.
              cell: "h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
            }}
          />
        </PopoverContent>
      </Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="View next day"
            onClick={() => goToObservationDate(nextDate)}
            disabled={isViewingToday}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Next day</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="hidden lg:inline-flex"
            aria-label="Open bulk entry"
            onClick={() => {
              trackEvent("bulk_entry_opened", { source: "header" });
              navigate(`${ROUTES.bulkEntry}?date=${viewedDate}`);
            }}
          >
            <Table2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Bulk entry</TooltipContent>
      </Tooltip>
    </>
  ) : isCalendarRoute && showPersonalChrome ? (
    calendarFullscreenAction
  ) : undefined;

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate(ROUTES.auth, { replace: true });
  };

  const SidebarSubscriptionStatusButton = () => {
    const { isMobile, setOpenMobile } = useSidebar();

    const handleClick = () => {
      if (!isSubscriptionManagementAvailable) {
        return;
      }

      if (isMobile) {
        setOpenMobile(false);
      }
      navigate(ROUTES.subscription);
    };

    return (
      <SidebarMenuButton
        isActive={pathname === ROUTES.subscription}
        tooltip="Access status"
        className="h-10"
        onClick={handleClick}
      >
        {subscriptionStatus === "past_due" ? (
          <AlertTriangle className="h-4 w-4 text-destructive" />
        ) : (
          <Crown className="h-4 w-4 text-amber-600" />
        )}
        <div className="min-w-0 group-data-[collapsible=icon]:hidden">
          <p className="truncate leading-tight">{subscriptionIndicatorLabel}</p>
          <p className="truncate text-xs opacity-70">Access</p>
        </div>
      </SidebarMenuButton>
    );
  };

  const SidebarGettingStartedButton = () => {
    const { isMobile, setOpenMobile } = useSidebar();

    const handleClick = () => {
      if (isMobile) {
        setOpenMobile(false);
      }
      navigate(ROUTES.gettingStarted);
    };

    return (
      <SidebarMenuButton
        isActive={pathname === ROUTES.gettingStarted}
        tooltip="Getting Started"
        className="h-10"
        onClick={handleClick}
      >
        <span className="relative">
          <Sparkles className="h-4 w-4" />
          {hasWhatsNew && <NewBadge variant="dot" className="absolute -right-1 -top-1" />}
        </span>
        <span>Getting Started</span>
      </SidebarMenuButton>
    );
  };

  const SidebarSupportButton = () => {
    const { isMobile, setOpenMobile } = useSidebar();
    const { openSupportChat, unreadCount } = useSupportChatPanel();

    return (
      <>
        <SidebarMenuButton
          tooltip={unreadCount > 0 ? "Feedback & Support — new reply" : "Feedback & Support"}
          className="h-10"
          onClick={() => {
            if (isMobile) {
              setOpenMobile(false);
            }
            openSupportChat();
          }}
        >
          <div className="relative">
            <LifeBuoy className="h-4 w-4" />
            {/* The count badge is hidden when the sidebar collapses to icons, so the dot on
                the icon itself is what carries the signal in that state. */}
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-destructive"
              />
            )}
          </div>
          <span>Feedback &amp; Support</span>
        </SidebarMenuButton>
        {unreadCount > 0 && (
          <SidebarMenuBadge className="bg-destructive text-destructive-foreground">
            <span className="sr-only">Unread support replies:</span>
            {unreadCount > 9 ? "9+" : unreadCount}
          </SidebarMenuBadge>
        )}
      </>
    );
  };

  return (
    <SupportChatProvider>
    <SidebarProvider defaultOpen className="h-full min-h-0 overflow-hidden">
      <Sidebar
        collapsible="icon"
        variant="sidebar"
        className="border-r border-sidebar-border bg-sidebar"
      >
        <SidebarHeader className="gap-0 p-0">
          <div className="flex items-center gap-2 rounded-md px-3 pt-3">
            <HeartCalendarIcon className="w-7 rounded-sm object-cover" />
            <div className="min-w-0 group-data-[collapsible=icon]:hidden">
              <p className="truncate text-base font-semibold">{sidebarDisplayName}</p>
            </div>
          </div>
        </SidebarHeader>

        <AppSidebarNav />

        <SidebarFooter>
          <SidebarMenu>
            {subscriptionIndicatorLabel && (
              <SidebarMenuItem>
                <SidebarSubscriptionStatusButton />
              </SidebarMenuItem>
            )}
            {showGettingStartedFooterLink && showPersonalChrome && (
              <SidebarMenuItem>
                <SidebarGettingStartedButton />
              </SidebarMenuItem>
            )}
            <SidebarMenuItem>
              <SidebarSupportButton />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Log out">
                <button type="button" className="h-10 w-full" onClick={handleSignOut}>
                  <LogOut className="h-4 w-4" />
                  <span>Log Out</span>
                </button>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>

      <SidebarInset className="relative h-full min-h-0 overflow-hidden bg-transparent">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-background to-muted"
        />
        <AppPageHeader title={title} subtitle={subtitle} actions={headerActions} />
        {viewingClientUserId ? <ClientViewBanner /> : null}
        <div className="relative flex min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain">
          <div className="compact-density-mobile safe-scroll-content mx-auto w-full max-w-3xl px-4 pt-4 md:px-5 md:pt-6">
            <div className="space-y-2 pb-6">
              <AnalyticsPersonProfileSync subscription={subscription} />
              <Outlet />
              {showFooterDisclaimer && (
                <HealthDisclaimer compact showSources={false} />
              )}
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
    <PostHogSurveyModal
      survey={activeSurvey}
      isOpen={isSurveyOpen}
      onClose={handleSurveyDismiss}
      onSubmit={handleSurveySubmit}
    />
    </SupportChatProvider>
  );
};

export default AppShellLayout;
